import { useCallback } from 'react';
import _ from 'underscore';
import odata from '@bingads-webui-static-legacy/communication/channel/odata';
import { dispatchPoll } from '@bingads-webui-universal/poll';
import { useCampaignFiltering } from './use-campaign-filtering';
import { AUTO_BIDDING_FEATURE_IDS } from '../constants';

const BID_STRATEGY_TYPES = Object.freeze({
  MAX_CLICKS: 2,
  MAX_CONVERSIONS: 3,
});

const FEATURE_ID_TO_BID_STRATEGY_TYPE = Object.freeze({
  [AUTO_BIDDING_FEATURE_IDS.MAX_CLICKS]: BID_STRATEGY_TYPES.MAX_CLICKS,
  [AUTO_BIDDING_FEATURE_IDS.MAX_CONVERSIONS]: BID_STRATEGY_TYPES.MAX_CONVERSIONS,
});

const TASK_STATUS = Object.freeze({
  COMPLETED: 'Completed',
  COMPLETED_WITH_ERRORS: 'CompletedWithErrors',
  PENDING_FILE_UPLOAD: 'PendingFileUpload',
});

const baseBulkActionPayload = {
  Entity: 'Campaign',
  BatchAction: {
    Actions: [
      {
        '@odata.type': '#Model.TextAction',
        Property: 'BiddingScheme',
        ActionType: 'Set',
        Value: null,
      },
    ],
  },
  Selection: {
    QueryString: null,
    CampaignId: null,
    AdGroupId: null,
  },
  Description: null,
  PreviewCompositeIds: [],
};

const getJob = jobId => `/Advertiser/Customers()/Accounts()/BulkEditSessions('${jobId}')?`;

const verifyTaskCompletion = response => response.Status === TASK_STATUS.COMPLETED || response.Status === TASK_STATUS.COMPLETED_WITH_ERRORS;

const onTaskSuccess = (data) => {
  const { Id, Status } = data;

  if (Status === TASK_STATUS.PENDING_FILE_UPLOAD) {
    return dispatchPoll(() => odata.get(getJob(Id)), verifyTaskCompletion);
  }

  return data;
};

export const useBulkSession = (coupon) => {
  const {
    campaignTypeFiltering, biddingStrategyTypeFiltering, and, toQueryString, selectCampaignsIds,
  } = useCampaignFiltering(coupon);

  const getBulkActionPayload = useCallback((selectedCampaigns) => {
    baseBulkActionPayload.BatchAction.Actions[0].Value = JSON.stringify({ Type: FEATURE_ID_TO_BID_STRATEGY_TYPE[coupon.FeatureId] });

    if (_.isArray(selectedCampaigns) && selectedCampaigns.length > 0) {
      baseBulkActionPayload.Selection.QueryString = toQueryString(selectCampaignsIds(selectedCampaigns.map(camp => camp.id)));
    } else {
      baseBulkActionPayload.Selection.QueryString = toQueryString(and(campaignTypeFiltering, biddingStrategyTypeFiltering));
    }

    return baseBulkActionPayload;
  }, [and, biddingStrategyTypeFiltering, campaignTypeFiltering, coupon.FeatureId, selectCampaignsIds, toQueryString]);

  const updateCampaigns = useCallback((selectedCampaigns) => {
    const bulkActionPayload = getBulkActionPayload(selectedCampaigns);

    return odata.post('Advertiser/Customers()/Accounts()/BulkEditSessions?', JSON.stringify(bulkActionPayload))
      .then(onTaskSuccess);
  }, [getBulkActionPayload]);

  return {
    updateCampaigns,
  };
};
